import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import WebhookTestButton from './WebhookTestButton';
import './WebhookMonitoringDashboard.css';

const WebhookMonitoringDashboard = () => {
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [realtimeActivity, setRealtimeActivity] = useState(null);
  const [cronStats, setCronStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cron');
  const [filters, setFilters] = useState({
    eventType: '',
    status: '',
    page: 1,
    limit: 50,
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [stats, setStats] = useState(null);
  const refreshInterval = useRef(null);

  // Fetch cron job statistics
  const fetchCronStats = async () => {
    try {
      const response = await axios.get('/api/heygen/cron-stats');
      if (response.data.success) {
        setCronStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching cron stats:', error);
    }
  };

  // Fetch webhook logs
  const fetchWebhookLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
        ...(filters.eventType && { eventType: filters.eventType }),
        ...(filters.status && { status: filters.status }),
      });

      const response = await axios.get(`/api/heygen/webhook-logs?${params}`);
      if (response.data.success) {
        setWebhookLogs(response.data.data.logs);
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    }
  };

  // Fetch realtime activity
  const fetchRealtimeActivity = async () => {
    try {
      const response = await axios.get('/api/heygen/webhook-activity?minutes=60');
      if (response.data.success) {
        setRealtimeActivity(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching realtime activity:', error);
    }
  };

  // Initialize and setup auto-refresh
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCronStats(),
        fetchWebhookLogs(), 
        fetchRealtimeActivity()
      ]);
      setLoading(false);
    };

    loadData();

    // Setup auto-refresh
    if (autoRefresh) {
      refreshInterval.current = setInterval(() => {
        fetchCronStats();
        if (activeTab === 'webhooks') {
          fetchWebhookLogs();
          fetchRealtimeActivity();
        }
      }, 5000); // Refresh every 5 seconds
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh, filters, activeTab]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed': return '#28a745';
      case 'received': return '#17a2b8';
      case 'error': return '#dc3545';
      case 'running': return '#28a745';
      case 'stopped': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processed': return '✅';
      case 'received': return '📨';
      case 'error': return '❌';
      case 'running': return '🟢';
      case 'stopped': return '🔴';
      default: return '❓';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatProcessingTime = (time) => {
    if (time < 1000) return `${time}ms`;
    return `${(time / 1000).toFixed(2)}s`;
  };

  if (loading) {
    return (
      <div className="webhook-dashboard-loading">
        <div className="spinner"></div>
        <p>Loading system monitoring dashboard...</p>
      </div>
    );
  }

  return (
    <div className="webhook-monitoring-dashboard">
      <div className="dashboard-header">
        <h2>🔔 System Monitoring Dashboard</h2>
        <div className="dashboard-controls">
          <div className="tab-buttons">
            <button 
              className={`tab-btn ${activeTab === 'cron' ? 'active' : ''}`}
              onClick={() => setActiveTab('cron')}
            >
              ⚙️ Cron Jobs
            </button>
            <button 
              className={`tab-btn ${activeTab === 'webhooks' ? 'active' : ''}`}
              onClick={() => setActiveTab('webhooks')}
            >
              🔔 Webhooks
            </button>
          </div>
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto Refresh (5s)
          </label>
          <button onClick={() => { 
            fetchCronStats(); 
            fetchWebhookLogs(); 
            fetchRealtimeActivity(); 
          }}>
            🔄 Refresh Now
          </button>
        </div>
      </div>

      {/* Cron Job Monitoring Tab */}
      {activeTab === 'cron' && cronStats && (
        <div className="cron-monitoring-section">
          <div className="cron-status-card">
            <h3>⚙️ Video Status Cron Job</h3>
            <div className="cron-status-grid">
              <div className="status-item">
                <span className="label">Status:</span>
                <span className={`status-badge ${cronStats.isRunning ? 'running' : 'stopped'}`}>
                  {getStatusIcon(cronStats.isRunning ? 'running' : 'stopped')} 
                  {cronStats.isRunning ? 'Running' : 'Stopped'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Last Run:</span>
                <span className="value">
                  {cronStats.lastRun ? formatTimestamp(cronStats.lastRun) : 'Never'}
                </span>
              </div>
              <div className="status-item">
                <span className="label">Total Checked:</span>
                <span className="value">{cronStats.totalChecked}</span>
              </div>
              <div className="status-item">
                <span className="label">Total Updated:</span>
                <span className="value">{cronStats.totalUpdated}</span>
              </div>
              <div className="status-item">
                <span className="label">Total Errors:</span>
                <span className="value">{cronStats.totalErrors}</span>
              </div>
            </div>
          </div>

          <div className="cron-info-section">
            <h4>📋 Cron Job Information</h4>
            <div className="info-grid">
              <div className="info-card">
                <h5>🕐 Schedule</h5>
                <p>Runs every 1 minute to check pending video status</p>
              </div>
              <div className="info-card">
                <h5>🎯 Purpose</h5>
                <p>Automatically monitors HeyGen video generation progress and updates database when videos are completed</p>
              </div>
              <div className="info-card">
                <h5>⚡ Performance</h5>
                <p>Only checks videos from the last 24 hours for optimal performance</p>
              </div>
              <div className="info-card">
                <h5>🔄 Auto-Recovery</h5>
                <p>Handles API failures gracefully and auto-fails videos that exceed 2-hour processing time</p>
              </div>
            </div>
          </div>

          <div className="cron-benefits">
            <h4>✅ Benefits of Cron-Based Monitoring</h4>
            <ul>
              <li><strong>Reliable:</strong> No dependency on external webhooks</li>
              <li><strong>Automatic:</strong> Runs continuously without manual intervention</li>
              <li><strong>Efficient:</strong> Smart scheduling to avoid unnecessary API calls</li>
              <li><strong>Resilient:</strong> Handles errors and timeouts gracefully</li>
              <li><strong>Scalable:</strong> Adapts to video volume automatically</li>
            </ul>
          </div>
        </div>
      )}

      {/* Webhook Monitoring Tab */}
      {activeTab === 'webhooks' && (
        <div className="webhook-section">
          {/* Real-time Statistics */}
          {stats && (
            <div className="stats-section">
              <h3>📊 Webhook Statistics</h3>
              <div className="stats-grid">
                <div className="stat-card total">
                  <h4>Total Webhooks</h4>
                  <div className="stat-number">{stats.total}</div>
                </div>
                <div className="stat-card processed">
                  <h4>Processed</h4>
                  <div className="stat-number">{stats.processed}</div>
                </div>
                <div className="stat-card received">
                  <h4>Received</h4>
                  <div className="stat-number">{stats.received}</div>
                </div>
                <div className="stat-card error">
                  <h4>Errors</h4>
                  <div className="stat-number">{stats.error}</div>
                </div>
                <div className="stat-card processing-time">
                  <h4>Avg Processing Time</h4>
                  <div className="stat-number">{formatProcessingTime(stats.avgProcessingTime)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Real-time Activity */}
          {realtimeActivity && (
            <div className="realtime-section">
              <h3>⚡ Real-time Activity (Last Hour)</h3>
              <div className="realtime-stats">
                <div className="realtime-stat">
                  <span className="label">Total Events:</span>
                  <span className="value">{realtimeActivity.summary.totalInPeriod}</span>
                </div>
                <div className="realtime-stat">
                  <span className="label">Success Rate:</span>
                  <span className="value">{realtimeActivity.summary.successRate}%</span>
                </div>
                <div className="realtime-stat">
                  <span className="label">Avg Processing:</span>
                  <span className="value">{realtimeActivity.summary.avgProcessingTime}ms</span>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="recent-activity">
                <h4>🕐 Recent Webhook Events</h4>
                <div className="activity-timeline">
                  {realtimeActivity.recentLogs.slice(0, 10).map((log) => (
                    <div key={log._id} className={`activity-item ${log.status}`}>
                      <div className="activity-icon">
                        {getStatusIcon(log.status)}
                      </div>
                      <div className="activity-details">
                        <div className="activity-title">
                          {log.eventType} - {log.videoId}
                        </div>
                        <div className="activity-meta">
                          <span className="timestamp">{formatTimestamp(log.createdAt)}</span>
                          <span className="processing-time">
                            {formatProcessingTime(log.processingTime)}
                          </span>
                          {log.error && (
                            <span className="error-message">{log.error}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="filters-section">
            <div className="filters-header">
              <h3>🔍 Webhook Logs</h3>
              <WebhookTestButton />
            </div>
            <div className="filters">
              <select
                value={filters.eventType}
                onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.value, page: 1 }))}
              >
                <option value="">All Event Types</option>
                <option value="avatar_video.success">avatar_video.success</option>
                <option value="avatar_video.fail">avatar_video.fail</option>
                <option value="test">test</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              >
                <option value="">All Statuses</option>
                <option value="received">Received</option>
                <option value="processed">Processed</option>
                <option value="error">Error</option>
              </select>
            </div>
          </div>

          {/* Webhook Logs Table */}
          <div className="logs-section">
            <div className="logs-table-container">
              <table className="logs-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Event Type</th>
                    <th>Video ID</th>
                    <th>Timestamp</th>
                    <th>Processing Time</th>
                    <th>IP Address</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {webhookLogs.map((log) => (
                    <tr key={log._id} className={`log-row ${log.status}`}>
                      <td>
                        <span 
                          className={`status-badge ${log.status}`}
                          style={{ backgroundColor: getStatusColor(log.status) }}
                        >
                          {getStatusIcon(log.status)} {log.status}
                        </span>
                      </td>
                      <td className="event-type">{log.eventType}</td>
                      <td className="video-id">
                        <code>{log.videoId}</code>
                      </td>
                      <td className="timestamp">
                        {formatTimestamp(log.createdAt)}
                      </td>
                      <td className="processing-time">
                        {formatProcessingTime(log.processingTime)}
                      </td>
                      <td className="ip-address">{log.ipAddress || 'N/A'}</td>
                      <td className="actions">
                        <button 
                          className="view-details-btn"
                          onClick={() => {
                            // Show detailed payload in modal or expand row
                            console.log('Webhook details:', log);
                            alert(`Payload: ${JSON.stringify(log.payload, null, 2)}`);
                          }}
                        >
                          👁️ Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {webhookLogs.length === 0 && (
              <div className="no-logs">
                <h4>📭 No webhook logs found</h4>
                <p>Webhook events will appear here when they are received from HeyGen.</p>
                <div className="webhook-info">
                  <h5>💡 Note:</h5>
                  <p>The system now uses <strong>cron-based monitoring</strong> instead of webhooks for better reliability. Check the "Cron Jobs" tab for system status.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookMonitoringDashboard;